import React, { useState, useEffect, useCallback } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";
const G = "#0077B5";
const BG = "#f3f2ef";
const BORDER = "rgba(0,0,0,0.1)";
const CARD = "#FFFFFF";

function authHeaders() {
  const st = localStorage.getItem("nectar_st");
  return { "Content-Type": "application/json", "X-Session-Token": st };
}

function fmtNum(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 1000) return (n / 1000).toFixed(1) + "K";
  return String(n || 0);
}

// Simple bar chart — no external dependency needed
function MiniBar({ data, height = 80, color = G }) {
  if (!data || data.length === 0) return <div style={{ height, background: BG, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#999" }}>No data yet</div>;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height }}>
      {data.slice(-30).map((d, i) => (
        <div
          key={d.date || i}
          title={`${d.date}: ${d.count} clicks`}
          style={{ flex: 1, background: color, borderRadius: "3px 3px 0 0", minWidth: 4, height: Math.max(2, (d.count / max) * height), transition: "height 0.3s" }}
        />
      ))}
    </div>
  );
}

function StatBox({ label, value, sub }) {
  return (
    <div style={{ background: BG, borderRadius: 10, padding: 14, textAlign: "center" }}>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function TopList({ title, items, labelKey, valueKey, valueLabel = "clicks" }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 10, padding: 14, marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 10 }}>{title}</div>
      {items.map((item, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: i < items.length - 1 ? "0.5px solid " + BORDER : "none", fontSize: 13 }}>
          <span style={{ color: "#333", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {i + 1}. {item[labelKey] || "—"}
          </span>
          <span style={{ color: G, fontWeight: 600, marginLeft: 12 }}>{fmtNum(item[valueKey])} {valueLabel}</span>
        </div>
      ))}
    </div>
  );
}

function DevicePie({ devices }) {
  const total = (devices?.mobile || 0) + (devices?.desktop || 0) + (devices?.tablet || 0);
  if (total === 0) return <div style={{ fontSize: 12, color: "#999", padding: 14 }}>No device data</div>;
  const items = [
    { label: "Mobile", count: devices.mobile || 0, color: "#0077B5" },
    { label: "Desktop", count: devices.desktop || 0, color: "#1D9E75" },
    { label: "Tablet", count: devices.tablet || 0, color: "#F5A623" },
  ].filter(d => d.count > 0);

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
      {/* Simple horizontal bar instead of pie */}
      <div style={{ flex: 1, height: 10, borderRadius: 5, overflow: "hidden", display: "flex", background: "#eee" }}>
        {items.map(d => (
          <div key={d.label} style={{ width: ((d.count / total) * 100) + "%", background: d.color, height: "100%" }} />
        ))}
      </div>
      <div style={{ display: "flex", gap: 12, fontSize: 11 }}>
        {items.map(d => (
          <span key={d.label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: d.color, display: "inline-block" }} />
            {d.label} {Math.round((d.count / total) * 100)}%
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Tabs ────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "by-member", label: "By Member" },
  { id: "by-plug", label: "By Plug" },
  { id: "by-link", label: "By Link" },
];

function OverviewTab({ data }) {
  if (!data) return null;
  if (!data.total_clicks_all_time) {
    return (
      <div style={{ padding: 60, textAlign: "center", background: "#F9FAFB", borderRadius: 12, border: "1px dashed #D1D5DB" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>&#128202;</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#374151", marginBottom: 8 }}>No analytics data yet</div>
        <div style={{ fontSize: 14, color: "#6B7280", maxWidth: 400, margin: "0 auto" }}>
          Plug post clicks will appear here once team members start posting and their audience engages with the links.
        </div>
      </div>
    );
  }
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 20 }}>
        <StatBox label="Clicks (all time)" value={fmtNum(data.total_clicks_all_time)} />
        <StatBox label="Clicks (30 days)" value={fmtNum(data.total_clicks_30d)} />
        <StatBox label="Clicks (7 days)" value={fmtNum(data.total_clicks_7d)} />
      </div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 8 }}>Clicks over time (30 days)</div>
        <MiniBar data={data.clicks_by_day} height={80} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
        <TopList title="Top plugs" items={data.top_plugs} labelKey="plug_id" valueKey="clicks" />
        <TopList title="Top members" items={data.top_members} labelKey="user_id" valueKey="clicks" />
      </div>
      <TopList title="Top links" items={data.top_links} labelKey="short_url" valueKey="clicks" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
        <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 8 }}>Referrer split</div>
          {data.referrer_split && Object.entries(data.referrer_split).map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
              <span>{k}</span><span style={{ fontWeight: 600 }}>{fmtNum(v)}</span>
            </div>
          ))}
        </div>
        <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 8 }}>Device split</div>
          <DevicePie devices={data.device_split} />
        </div>
      </div>
    </div>
  );
}

function ByMemberTab({ workspaceId, members }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadMember = async (uid) => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/workspace/${workspaceId}/member/${uid}/plug-analytics`, { headers: authHeaders() });
      if (r.ok) setDetail(await r.json());
    } catch {}
    setLoading(false);
  };

  if (detail) {
    return (
      <div>
        <button onClick={() => setDetail(null)} style={{ background: "transparent", border: "none", color: G, fontSize: 13, cursor: "pointer", marginBottom: 12, padding: 0 }}>← Back to all members</button>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>{detail.member_name} <span style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>({detail.role_type})</span></div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 16 }}>
          <StatBox label="Total clicks" value={fmtNum(detail.total_clicks)} />
          <StatBox label="Plugs posted" value={detail.total_plugs_posted} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 8 }}>Clicks over time</div>
          <MiniBar data={detail.clicks_by_day} height={60} />
        </div>
        <TopList title="Clicks by plug" items={detail.clicks_by_plug} labelKey="plug_id" valueKey="clicks" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>Click a member for drill-down</div>
      <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 10, overflow: "hidden" }}>
        {(members || []).map((m, i) => (
          <div key={m.user_id} onClick={() => loadMember(m.user_id)} style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", borderBottom: i < members.length - 1 ? "0.5px solid " + BORDER : "none", cursor: "pointer", fontSize: 13 }}>
            <span style={{ fontWeight: 500 }}>{m.user_id}</span>
            <span style={{ color: G, fontWeight: 600 }}>{fmtNum(m.clicks)} clicks</span>
          </div>
        ))}
        {(!members || members.length === 0) && <div style={{ padding: 14, fontSize: 13, color: "#888", textAlign: "center" }}>No member data yet</div>}
      </div>
      {loading && <div style={{ padding: 12, fontSize: 12, color: "#888" }}>Loading…</div>}
    </div>
  );
}

function ByPlugTab({ workspaceId, plugs }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadPlug = async (pid) => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/workspace/${workspaceId}/plug/${pid}/analytics`, { headers: authHeaders() });
      if (r.ok) setDetail(await r.json());
    } catch {}
    setLoading(false);
  };

  if (detail) {
    return (
      <div>
        <button onClick={() => setDetail(null)} style={{ background: "transparent", border: "none", color: G, fontSize: 13, cursor: "pointer", marginBottom: 12, padding: 0 }}>← Back to all plugs</button>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{detail.plug_type}</div>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>{(detail.base_caption || "").slice(0, 200)}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 16 }}>
          <StatBox label="Total clicks" value={fmtNum(detail.total_clicks)} />
          <StatBox label="Unique clicks" value={fmtNum(detail.unique_clicks)} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 8 }}>Clicks over time</div>
          <MiniBar data={detail.clicks_by_day} height={60} />
        </div>
        <TopList title="Clicks by member" items={detail.clicks_by_member} labelKey="user_id" valueKey="clicks" />
        <DevicePie devices={detail.device_split} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>Click a plug for drill-down</div>
      <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 10, overflow: "hidden" }}>
        {(plugs || []).map((p, i) => (
          <div key={p.plug_id} onClick={() => loadPlug(p.plug_id)} style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", borderBottom: i < plugs.length - 1 ? "0.5px solid " + BORDER : "none", cursor: "pointer", fontSize: 13 }}>
            <span style={{ fontWeight: 500 }}>{p.plug_id}</span>
            <span style={{ color: G, fontWeight: 600 }}>{fmtNum(p.clicks)} clicks</span>
          </div>
        ))}
        {(!plugs || plugs.length === 0) && <div style={{ padding: 14, fontSize: 13, color: "#888", textAlign: "center" }}>No plug data yet</div>}
      </div>
      {loading && <div style={{ padding: 12, fontSize: 12, color: "#888" }}>Loading…</div>}
    </div>
  );
}

function ByLinkTab({ workspaceId }) {
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/app/api/workspace/${workspaceId}/links/top`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : { links: [] })
      .then(d => { setLinks(d.links || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [workspaceId]);

  if (loading) return <div style={{ padding: 14, fontSize: 12, color: "#888" }}>Loading links…</div>;

  return (
    <div>
      <div style={{ background: CARD, border: "0.5px solid " + BORDER, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 0, padding: "8px 14px", background: BG, borderBottom: "0.5px solid " + BORDER }}>
          {["SHORT URL", "DESTINATION", "CLICKS", "CREATED"].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 }}>{h}</div>
          ))}
        </div>
        {(links || []).map((l, i) => (
          <div key={l.short_code} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 0, padding: "8px 14px", borderBottom: i < links.length - 1 ? "0.5px solid " + BORDER : "none", fontSize: 12 }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <a href={l.short_url} target="_blank" rel="noopener noreferrer" style={{ color: G, textDecoration: "none" }}>{l.short_url}</a>
            </div>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#888" }}>{l.original_url}</div>
            <div style={{ fontWeight: 600 }}>{fmtNum(l.clicks)}</div>
            <div style={{ color: "#888" }}>{l.created_at ? new Date(l.created_at).toLocaleDateString() : ""}</div>
          </div>
        ))}
        {(!links || links.length === 0) && <div style={{ padding: 14, fontSize: 13, color: "#888", textAlign: "center" }}>No links generated yet</div>}
      </div>
    </div>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────

export default function PlugAnalyticsSection({ workspaceId }) {
  const [tab, setTab] = useState("overview");
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/workspace/${workspaceId}/plug-analytics`, { headers: authHeaders() });
      if (r.ok) setOverviewData(await r.json());
    } catch {}
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ marginBottom: 28 }} data-testid="plug-analytics-section">
      <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 16 }}>Plug analytics</div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid " + BORDER, marginBottom: 16 }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-analytics-${t.id}`}
            style={{
              background: "transparent",
              border: "none",
              borderBottom: tab === t.id ? `2px solid ${G}` : "2px solid transparent",
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: tab === t.id ? 600 : 400,
              color: tab === t.id ? G : "#666",
              cursor: "pointer",
              fontFamily: "inherit",
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 20, textAlign: "center", color: "#888" }}>Loading analytics…</div>
      ) : (
        <>
          {tab === "overview" && <OverviewTab data={overviewData} />}
          {tab === "by-member" && <ByMemberTab workspaceId={workspaceId} members={overviewData?.top_members} />}
          {tab === "by-plug" && <ByPlugTab workspaceId={workspaceId} plugs={overviewData?.top_plugs} />}
          {tab === "by-link" && <ByLinkTab workspaceId={workspaceId} />}
        </>
      )}
    </div>
  );
}
